export const KPI_KINDS = ['$','#','%']; 
export const KPI_DIRECTIONS = [1, -1];
export const KPI_IMAGE_PARAMS = ["image", "picture", "photo"];
export const KPI_FORM_INPUT_TYPES =  ["text", "image", "number", "date", "checkbox", "picture", "link", "dropdown", "notification_settings"];
/**
 * @constant {["progressive", "anchor", "display"]}
 */
export const KPI_TYPES = ["progressive", "anchor", "display"];
export const MAX_WORKSPACE_KPIS = 10;

/**
 * Workspace metrics use this to identify if a KPI is gamified (live scoring).
 * A metric is gamified when: metricType is not "display", isDraft is false, and disabled is not true.
 * @param {Object} kpi - KPI object with metricType, isDraft, disabled
 * @returns {boolean}
 */
export function isKpiGamified(kpi) {
  if (!kpi) return false;
  const { metricType, isDraft, disabled } = kpi;
  return metricType !== "display" && metricType != null && !isDraft && disabled !== true;
}

/** Metric is admin-entered in Scorekeeper only — hidden from public KPI columns/cards, but points still count toward totals. */
export function isKpiScorekeeperOnly(kpi) {
  return !!kpi?.parameters?.scorekeeperOnly;
}

/** Resolve the key used in usersKpis month objects (handles string/ObjectId mismatches). */
export function resolveMonthKpiKey(monthObj, kpiId) {
  if (!monthObj || kpiId == null) return kpiId;
  if (monthObj[kpiId]) return kpiId;
  const match = Object.keys(monthObj).find(
    (key) => key !== "total" && String(key) === String(kpiId),
  );
  return match || kpiId;
}

const COMPOSITE_OPERAND_FNS = {
  add: (a, b) => a + b,
  subtract: (a, b) => a - b,
  multiply: (a, b) => a * b,
  divide: (a, b) => (b === 0 ? 0 : a / b),
};

/** Apply a composite operand to two amounts. Returns 0 for unknown operand or non-finite result. */
export function applyCompositeOperand(amount1, amount2, operand) {
  const fn = COMPOSITE_OPERAND_FNS[operand];
  if (!fn) return 0;
  const result = fn(Number(amount1) || 0, Number(amount2) || 0);
  return Number.isFinite(result) ? result : 0;
}

/** Composite KPIs calculated from two source metrics (read-only in Scorekeeper). */
export function isCompositeKpi(kpi) {
  if (!kpi) return false;
  const opt = kpi.optional;
  return !!(opt?.sourceId1 && opt?.sourceId2 && opt?.operand);
}

/** Resolve source metric titles for Scorekeeper composite hover (array of KPIs, not id→name map). */
export function getCompositeFormulaLabel(kpi, groupKpis = []) {
  const opt = kpi?.optional;
  if (!isCompositeKpi(kpi)) return null;
  const list = Array.isArray(groupKpis) ? groupKpis : [];
  const findTitle = (id) => {
    const match = list.find((k) => String(k?._id) === String(id));
    return match?.title || match?.name || "Metric";
  };
  const opMap = { divide: "÷", multiply: "×", add: "+", subtract: "−" };
  const op = opMap[opt.operand] || opt.operand;
  return `${findTitle(opt.sourceId1)} ${op} ${findTitle(opt.sourceId2)}`;
}

/** KPI columns/cards on dashboard, TV, and leaderboards — excludes scorekeeper-only metrics. */
export function filterKpisForPublicDisplay(kpis) {
  if (!Array.isArray(kpis)) return kpis;
  return kpis.filter((k) => !isKpiScorekeeperOnly(k));
}

/** Max metrics shown Live on the workspace dashboard. */
export const LIVE_DASHBOARD_CAP = 5;

/**
 * The Live set — the metrics that show on the dashboard. Mirrors the Workspace
 * metrics page exactly: an explicit parameters.featured true/false always wins;
 * metrics that were never toggled fill the remaining slots up to the cap,
 * gamified ones first. Returns the kpis in their original order, filtered.
 */
export function filterLiveKpis(kpis, cap = LIVE_DASHBOARD_CAP) {
  if (!Array.isArray(kpis)) return [];
  const explicitLive = new Set();
  const explicitOff = new Set();
  kpis.forEach((k) => {
    if (k?.parameters?.featured === true) explicitLive.add(k);
    else if (k?.parameters?.featured === false) explicitOff.add(k);
  });
  let slots = Math.max(0, cap - explicitLive.size);
  const defaultLive = new Set();
  // Fill remaining slots with untoggled metrics, gamified first (same order the
  // Workspace metrics page lists them).
  [...kpis].sort((a, b) => (isKpiGamified(b) ? 1 : 0) - (isKpiGamified(a) ? 1 : 0)).forEach((k) => {
    if (slots <= 0 || explicitLive.has(k) || explicitOff.has(k)) return;
    defaultLive.add(k);
    slots -= 1;
  });
  return kpis.filter((k) => explicitLive.has(k) || defaultLive.has(k));
}

const COMPOSITE_OPERATORS = ["add", "subtract", "multiply", "divide"];

export function valueTypeToKind(valueType) {
  if (valueType === "monetary") return "$";
  if (valueType === "item") return "#";
  return "#";
}

/** Kind used for Scorekeeper total-score inputs — plain numbers, never normalized % storage. */
export function resolveTotalScoreEntryKind(kpi) {
  const valueType = kpi?.parameters?.valueType;
  if (valueType === "monetary") return "$";
  if (valueType === "item") return "#";
  const kind = kpi?.kind ?? kpi?.model?.kind;
  if (kind === "%") return "#";
  return kind ?? "#";
}

export function kindToValueType(kind) {
  if (kind === "$") return "monetary";
  if (kind === "#") return "item";
  return "monetary";
}

export function effectTypeToDirection(effectType) {
  return effectType === "negative" ? -1 : 1;
}

export function directionToEffectType(direction) {
  return direction === -1 ? "negative" : "positive";
}

/**
 * Rehydrate advanced gamification settings persisted on GroupKpi (optional + parameters).
 */
export function getAdvancedGameSettingsFromKpi(kpi) {
  const params = kpi?.parameters || {};
  const opt = kpi?.optional || {};
  const hasComposite = !!(opt.sourceId1 && opt.sourceId2);
  const kind = kpi?.kind ?? kpi?.model?.kind;
  const direction = kpi?.direction ?? kpi?.model?.direction;
  return {
    monitorMode: !!params.monitorMode,
    scorekeeperOnly: !!params.scorekeeperOnly,
    resetFrequency: params.resetFrequency || "daily",
    compositeMetric: params.compositeMetric === true,
    compositeConfig: hasComposite
      ? {
          metricAId: String(opt.sourceId1),
          metricBId: String(opt.sourceId2),
          operator: COMPOSITE_OPERATORS.includes(opt.operand) ? opt.operand : "add",
        }
      : undefined,
    pointsRule: params.pointsRule,
    valueType: params.valueType || kindToValueType(kind),
    effectType: params.effectType || directionToEffectType(direction),
  };
}

/**
 * Resolve points rule for gamified KPI cards/modals (stored parameters override top-level fields).
 */
export function resolveGamePointsRule(kpi, advancedSettings) {
  const settings = advancedSettings || getAdvancedGameSettingsFromKpi(kpi);
  const fromKpi = kpi?.pointsRule || kpi?.model?.pointsRule;
  const storedPointsRule = settings.pointsRule;

  let metricAmount = fromKpi?.metricAmount ?? kpi?.model?.defaultPointScale ?? 1;
  if (typeof storedPointsRule?.metricAmount === "number") {
    metricAmount = storedPointsRule.metricAmount;
  } else if (typeof kpi?.pointScale === "number") {
    metricAmount = kpi.pointScale;
  }

  let pointsEarned = fromKpi?.pointsEarned ?? kpi?.model?.defaultPointValue ?? 10;
  if (typeof storedPointsRule?.pointsEarned === "number") {
    pointsEarned = storedPointsRule.pointsEarned;
  } else if (typeof kpi?.pointValue === "number") {
    pointsEarned = kpi.pointValue;
  }

  return { metricAmount, pointsEarned };
}

/**
 * Map MetricSettingsModal gameConfig advanced fields onto a GroupKpi update payload.
 */
export function applyAdvancedGameSettingsToKpiPayload(kpiPayload, rawKpi, game) {
  if (!game || typeof game !== "object") return kpiPayload;

  kpiPayload.parameters = {
    ...(rawKpi?.parameters || {}),
    monitorMode: !!game.monitorMode,
    scorekeeperOnly: !!game.scorekeeperOnly,
    resetFrequency: game.resetFrequency || "daily",
    compositeMetric: !!game.compositeMetric,
    ...(game.valueType && { valueType: game.valueType }),
    ...(game.effectType && { effectType: game.effectType }),
    ...(game.pointsRule && { pointsRule: game.pointsRule }),
  };

  if (game.type !== "anchor") {
    if (game.valueType) {
      kpiPayload.kind = valueTypeToKind(game.valueType);
    }
    if (game.effectType) {
      kpiPayload.direction = effectTypeToDirection(game.effectType);
    }
  }

  if (
    game.compositeMetric &&
    game.compositeConfig?.metricAId &&
    game.compositeConfig?.metricBId
  ) {
    kpiPayload.dataSourceOne = game.compositeConfig.metricAId;
    kpiPayload.calculationMethod = game.compositeConfig.operator ?? "add";
    kpiPayload.dataSourceTwo = game.compositeConfig.metricBId;
  } else if (game.compositeMetric === false) {
    kpiPayload.clearComposite = true;
  }

  if (game.monitorMode) {
    kpiPayload.pointValue = 0;
  }

  return kpiPayload;
}

/**
 * Map MetricSettingsModal anchor gameConfig onto top-level GroupKpi fields the server
 * expects (buildCustomMetricData + optional.benchmarkPoint / aboveBenchmark / belowBenchmark).
 */
export function applyAnchorGameSettingsToKpiPayload(kpiPayload, game) {
  if (!game || game.type !== "anchor") return kpiPayload;

  const anchorUnitToKind = { percent: "%", currency: "$", number: "#" };
  const anchorUnit = game.anchorUnit || "percent";
  const kind = anchorUnitToKind[anchorUnit] || "%";
  const rawAnchorValue = Number(game.anchorValue);
  const safeAnchorValue = Number.isFinite(rawAnchorValue) ? rawAnchorValue : 0;
  const targetData = anchorUnit === "percent" ? safeAnchorValue / 100 : safeAnchorValue;
  // "Set Unit Value" — the increment size that pointsPerUnitAbove/Below scale
  // by (see calculateTotalAnchorMetricPoints in employeeKpi.js). Admin-configurable
  // via gameConfig.anchorUnitValue; falls back to 1 (or 1/100 for percent, since
  // percent targets are stored as a 0-1 fraction) when unset for backward compat.
  const rawUnitValue = Number(game.anchorUnitValue);
  const safeUnitValue = Number.isFinite(rawUnitValue) && rawUnitValue > 0 ? rawUnitValue : 1;
  const unitData = anchorUnit === "percent" ? safeUnitValue / 100 : safeUnitValue;
  const benchmarkPoint = Number(game.benchmarkPoints);
  const aboveBenchmark = Number(game.pointsPerUnitAbove);
  const belowBenchmark = Number(game.pointsPerUnitBelow);

  kpiPayload.metricType = "anchor";
  kpiPayload.target = targetData;
  kpiPayload.unit = unitData;
  kpiPayload.point = Number.isFinite(aboveBenchmark) ? aboveBenchmark : 0;
  kpiPayload.gap = true;
  kpiPayload.kind = kind;
  kpiPayload.anchorType = anchorUnit;
  kpiPayload.benchmarkPoint = Number.isFinite(benchmarkPoint) ? benchmarkPoint : 0;
  kpiPayload.aboveBenchmark = Number.isFinite(aboveBenchmark) ? aboveBenchmark : 0;
  kpiPayload.belowBenchmark = Number.isFinite(belowBenchmark) ? belowBenchmark : 0;

  return kpiPayload;
}
